import { useEffect, useState } from "react";
import { ChevronDown, Eye, Loader2, RotateCcw, Save, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";

const Pair = ({ d, set, k, label, area, max }) => {
  const C = area ? Textarea : Input;
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {["en", "fr"].map((l) => (
        <label key={l} className="block text-xs font-semibold text-slate-500">{label} ({l === "en" ? "English" : "French"})
          <C rows={area ? 5 : undefined} maxLength={max} value={d[`${k}_${l}`]} onChange={(e) => set(`${k}_${l}`, e.target.value)} className="mt-1" data-testid={`welcome-${k}-${l}`} />
        </label>
      ))}
    </div>
  );
};

export const WelcomeEmailSettings = () => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [prev, setPrev] = useState(null);
  const [lang, setLang] = useState("en");
  const [to, setTo] = useState("");
  useEffect(() => { if (open && !d) api.get("/admin/welcome-email").then(({ data }) => setD(data)).catch((e) => toast.error(errText(e))); }, [open, d]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") { await api.put("/admin/welcome-email", d); toast.success("Welcome email saved"); }
      else {
        const { data } = await api.post("/admin/welcome-email/preview", { ...d, lang, to: kind === "test" ? to : "" });
        setPrev(data);
        if (kind === "test") toast.success(`Test sent to ${to}`);
      }
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80" data-testid="welcome-email-settings">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left" data-testid="welcome-email-toggle">
        <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center"><Sparkles size={18} className="text-[#F5551E]" /></span>
        <div className="flex-1"><p className="text-sm font-semibold text-[#12234A]">Welcome email</p><p className="text-xs text-slate-400">Sent once to each new newsletter subscriber, in their language, with the newest pets.</p></div>
        <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (!d ? <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div> : (
        <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4">
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-3"><Switch checked={d.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="welcome-enabled" /><span className="text-sm font-semibold text-[#12234A]">Send welcome emails</span></label>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#12234A]">Newest pets to show
              <select value={d.pets_count} onChange={(e) => set("pets_count", Number(e.target.value))} className="h-9 rounded-lg border border-slate-200 px-2" data-testid="welcome-pets-count">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n === 0 ? "None" : n}</option>)}
              </select>
            </label>
          </div>
          <Pair d={d} set={set} k="subject" label="Subject" max={150} />
          <Pair d={d} set={set} k="heading" label="Heading" max={120} />
          <Pair d={d} set={set} k="message" label="Message" area max={2000} />
          <Pair d={d} set={set} k="button" label="Button text (leave empty to hide)" max={40} />
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button onClick={() => run("save")} disabled={!!busy} className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5" data-testid="welcome-save">{busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</Button>
            <Button variant="outline" onClick={() => { setD({ ...d.defaults, defaults: d.defaults }); toast("Original wording loaded — press Save to keep it"); }} className="rounded-xl gap-1.5" data-testid="welcome-reset"><RotateCcw size={15} /> Original</Button>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-2 text-sm" data-testid="welcome-preview-lang"><option value="en">English</option><option value="fr">French</option></select>
            <Button variant="outline" onClick={() => run("preview")} disabled={!!busy} className="rounded-xl gap-1.5" data-testid="welcome-preview">{busy === "preview" ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Preview</Button>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" className="h-10 w-52 rounded-xl" data-testid="welcome-test-to" />
            <Button variant="outline" onClick={() => run("test")} disabled={!!busy || !to} className="rounded-xl gap-1.5" data-testid="welcome-test-send">{busy === "test" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send test</Button>
          </div>
          {prev && (
            <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="welcome-preview-frame">
              <p className="px-4 py-2 text-xs bg-slate-50 border-b text-slate-500">Subject: <strong className="text-[#12234A]">{prev.subject}</strong></p>
              <iframe title="Welcome email preview" srcDoc={prev.html} className="w-full h-[560px] bg-white" sandbox="allow-same-origin" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
